import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Clock, Building2, FileText, Mail, UserCircle2, CheckCircle2, Link2,
  Tag as TagIcon, MessageCircle, Facebook, LogOut, UserCog, Settings as SettingsIcon,
  CalendarPlus, Upload, Trash2, Plus, X, Pencil, ShieldCheck,
} from "lucide-react";
import Tags from "./Tags";
import Staff from "./Staff";
import WhatsAppSection from "./WhatsAppSection";
import MetaPagesSection from "./MetaPagesSection";
import { MembersTab } from "./MembersTab";
import { LanguageCard } from "@/components/LanguageCard";

const DAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"];
const DAY_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

const emptyHours = () => Object.fromEntries(
  DAY_KEYS.map((k) => [k, k === "6"
    ? { closed: true, open: "10:00", close: "13:00", open2: "", close2: "" }
    : { closed: false, open: "09:00", close: "13:00", open2: "15:00", close2: "19:00" }
  ])
);

const SECTIONS = [
  { key: "account",   label: "settings.sections.account",  icon: UserCircle2 },
  { key: "business",  label: "settings.sections.business", icon: Building2 },
  { key: "fiscal",    label: "settings.sections.fiscal",   icon: FileText },
  { key: "email",     label: "settings.sections.email",    icon: Mail },
  { key: "whatsapp",  label: "settings.sections.whatsapp", icon: MessageCircle },
  { key: "meta",      label: "settings.sections.meta",     icon: Facebook },
  { key: "hours",     label: "settings.sections.hours",    icon: Clock },
  { key: "overrides", label: "settings.sections.overrides",icon: CalendarPlus },
  { key: "staff",     label: "settings.sections.staff",    icon: UserCog },
  { key: "access",    label: "settings.sections.access",   icon: ShieldCheck },
  { key: "tags",      label: "settings.sections.tags",     icon: TagIcon },
];

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Restore section from URL hash (e.g. #email) or default to account
  const initial = typeof window !== "undefined"
    ? (window.location.hash.replace("#", "") || "account")
    : "account";
  const [section, setSectionState] = useState(SECTIONS.some((s) => s.key === initial) ? initial : "account");
  const setSection = (k) => {
    setSectionState(k);
    window.history.replaceState(null, "", `#${k}`);
  };

  const [info, setInfo] = useState({ name: "", address: "", phone: "", email: "" });
  const [fiscal, setFiscal] = useState({ legal_form: "", vat_number: "", tax_code: "", sdi_code: "", pec: "", iban: "", tax_regime: "" });
  const [emailCfg, setEmailCfg] = useState({ sender_name: "", reply_to_email: "" });
  const [hours, setHours] = useState(emptyHours());

  const load = async () => {
    const t = (await api.get("/tenant")).data;
    setInfo({ name: t.name || "", address: t.address || "", phone: t.phone || "", email: t.email || "" });
    setFiscal({
      legal_form: t.legal_form || "", vat_number: t.vat_number || "", tax_code: t.tax_code || "",
      sdi_code: t.sdi_code || "", pec: t.pec || "", iban: t.iban || "",
      tax_regime: t.tax_regime || "",
    });
    setEmailCfg({ sender_name: t.sender_name || "", reply_to_email: t.reply_to_email || "" });
    setHours(t.opening_hours || emptyHours());
  };
  useEffect(() => { load(); }, []);

  const saveInfo = async (e) => {
    e.preventDefault();
    try { await api.patch("/tenant", { ...info, email: info.email || null }); toast.success("Dati salvati"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const saveFiscal = async (e) => {
    e.preventDefault();
    try { await api.patch("/tenant", fiscal); toast.success("Dati fiscali salvati"); load(); }
    catch { toast.error("Errore"); }
  };
  const saveEmailCfg = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/tenant", { sender_name: emailCfg.sender_name || null, reply_to_email: emailCfg.reply_to_email || null });
      toast.success("Impostazioni email salvate"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const saveHours = async () => {
    try { await api.patch("/tenant", { opening_hours: hours }); toast.success("Orari salvati"); load(); }
    catch { toast.error("Errore"); }
  };
  const setDay = (day, patch) => setHours((h) => ({ ...h, [day]: { ...h[day], ...patch } }));

  return (
    <div data-testid="settings-page" className="space-y-6">
      <header>
        <h1 className="font-display text-4xl font-black tracking-tight inline-flex items-center gap-3">
          <SettingsIcon className="h-7 w-7 text-[color:var(--sp-brand)]" />
          {t("nav.settings")}
        </h1>
      </header>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Sub-nav */}
        <aside className="sp-card p-2 h-fit sticky top-4">
          <nav className="space-y-0.5" data-testid="settings-subnav">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = section === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setSection(s.key)}
                  data-testid={`settings-tab-${s.key}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium text-left transition-colors ${
                    active ? "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" : "text-[color:var(--sp-text-secondary)] hover:bg-[color:var(--sp-subtle)]"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(s.label)}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Section content */}
        <div className="min-w-0">
          {section === "account" && <AccountCard user={user} />}
          {section === "business" && <BusinessSection info={info} setInfo={setInfo} onSave={saveInfo} />}
          {section === "fiscal" && <FiscalSection fiscal={fiscal} setFiscal={setFiscal} onSave={saveFiscal} />}
          {section === "email" && <EmailSection emailCfg={emailCfg} setEmailCfg={setEmailCfg} onSave={saveEmailCfg} salonName={info.name} />}
          {section === "whatsapp" && <WhatsAppSection />}
          {section === "meta" && <MetaPagesSection />}
          {section === "hours" && (
            <HoursSection hours={hours} setDay={setDay} onSave={saveHours} />
          )}
          {section === "overrides" && <OverridesSection />}
          {section === "staff" && (
            <div className="sp-card p-8"><Staff embedded /></div>
          )}
          {section === "access" && <MembersTab />}
          {section === "tags" && (
            <div className="sp-card p-8"><Tags embedded /></div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --- Sections --- */
export function BusinessSection({ info, setInfo, onSave }) {
  return (
    <form onSubmit={onSave} className="sp-card p-8 space-y-4">
      <div className="flex items-center gap-3">
        <Building2 className="h-5 w-5 text-[color:var(--sp-brand)]" />
        <h2 className="font-display text-xl font-black">Anagrafica salone</h2>
      </div>
      <div><label className="text-sm font-bold">Nome salone</label><input data-testid="settings-name-input" required className="sp-input mt-1" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} /></div>
      <div><label className="text-sm font-bold">Indirizzo</label><input data-testid="settings-address-input" placeholder="Via, numero civico, città, CAP" className="sp-input mt-1" value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-sm font-bold">Telefono</label><input data-testid="settings-phone-input" className="sp-input mt-1" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} /></div>
        <div><label className="text-sm font-bold">Email</label><input type="email" data-testid="settings-email-input" className="sp-input mt-1" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} /></div>
      </div>
      <button data-testid="settings-save-btn" className="sp-btn-primary">Salva anagrafica</button>
    </form>
  );
}

export function FiscalSection({ fiscal, setFiscal, onSave }) {
  return (
    <form onSubmit={onSave} className="sp-card p-8 space-y-4">
      <div className="flex items-center gap-3">
        <FileText className="h-5 w-5 text-[color:var(--sp-brand)]" />
        <h2 className="font-display text-xl font-black">Dati fiscali</h2>
      </div>
      <p className="text-sm text-[color:var(--sp-text-secondary)]">Dati necessari per fatturazione elettronica, report contabili e Calcolatore Tasse.</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-bold">Forma giuridica</label>
          <select className="sp-input mt-1" value={fiscal.legal_form} onChange={(e) => setFiscal({ ...fiscal, legal_form: e.target.value })}>
            <option value="">Seleziona…</option>
            <option value="Ditta individuale">Ditta individuale</option>
            <option value="SRL">SRL</option>
            <option value="SRLS">SRLS</option>
            <option value="SNC">SNC</option>
            <option value="SAS">SAS</option>
            <option value="Cooperativa">Cooperativa</option>
            <option value="Altro">Altro</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-bold">Regime fiscale</label>
          <select
            data-testid="settings-tax-regime"
            className="sp-input mt-1"
            value={fiscal.tax_regime || ""}
            onChange={(e) => setFiscal({ ...fiscal, tax_regime: e.target.value })}
          >
            <option value="">Seleziona…</option>
            <option value="forfettario_5">Forfettario · 5% (start-up)</option>
            <option value="forfettario_15">Forfettario · 15%</option>
            <option value="ordinario_semplificato">Ordinario semplificato (ditta individuale)</option>
            <option value="ordinario_srl">Ordinario (SRL/società)</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-sm font-bold">Partita IVA</label><input data-testid="settings-vat-input" placeholder="IT12345678901" className="sp-input mt-1" value={fiscal.vat_number} onChange={(e) => setFiscal({ ...fiscal, vat_number: e.target.value })} /></div>
        <div><label className="text-sm font-bold">Codice Fiscale</label><input data-testid="settings-taxcode-input" className="sp-input mt-1" value={fiscal.tax_code} onChange={(e) => setFiscal({ ...fiscal, tax_code: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-sm font-bold">Codice destinatario SDI</label><input placeholder="7 caratteri" className="sp-input mt-1" value={fiscal.sdi_code} onChange={(e) => setFiscal({ ...fiscal, sdi_code: e.target.value })} /></div>
        <div><label className="text-sm font-bold">PEC</label><input type="email" placeholder="pec@..." className="sp-input mt-1" value={fiscal.pec} onChange={(e) => setFiscal({ ...fiscal, pec: e.target.value })} /></div>
      </div>
      <div><label className="text-sm font-bold">IBAN</label><input placeholder="IT..." className="sp-input mt-1" value={fiscal.iban} onChange={(e) => setFiscal({ ...fiscal, iban: e.target.value })} /></div>
      <button className="sp-btn-primary">Salva dati fiscali</button>
    </form>
  );
}

export function EmailSection({ emailCfg, setEmailCfg, onSave, salonName }) {
  return (
    <form onSubmit={onSave} className="sp-card p-8 space-y-4">
      <div className="flex items-center gap-3">
        <Mail className="h-5 w-5 text-[color:var(--sp-brand)]" />
        <h2 className="font-display text-xl font-black">Email — Mittente</h2>
      </div>
      <p className="text-sm text-[color:var(--sp-text-secondary)]">
        Come apparirà il salone nelle email inviate ai clienti (newsletter, promo, automazioni).
        L&apos;indirizzo mittente tecnico è gestito da Memento — qui personalizzi il nome visibile.
      </p>
      <div>
        <label className="text-sm font-bold">Nome mittente visibile</label>
        <input
          data-testid="settings-sender-name"
          className="sp-input mt-1"
          value={emailCfg.sender_name}
          onChange={(e) => setEmailCfg({ ...emailCfg, sender_name: e.target.value })}
          placeholder={salonName || "Es. Il Salone di Maria"}
        />
        <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1">
          Se lasciato vuoto verrà usato il nome del salone (&quot;{salonName || "—"}&quot;).
        </div>
      </div>
      <div>
        <label className="text-sm font-bold">Email di risposta (Reply-To)</label>
        <input
          data-testid="settings-reply-to"
          type="email"
          className="sp-input mt-1"
          value={emailCfg.reply_to_email}
          onChange={(e) => setEmailCfg({ ...emailCfg, reply_to_email: e.target.value })}
          placeholder="prenotazioni@ilsalonedimaria.it"
        />
        <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1">
          Quando un cliente risponde all&apos;email, la risposta arriva qui. Consigliato: la tua casella di lavoro.
        </div>
      </div>
      <button data-testid="settings-email-save-btn" className="sp-btn-primary">Salva impostazioni email</button>
    </form>
  );
}

function HoursSection({ hours, setDay, onSave }) {
  return (
    <div className="sp-card p-8 space-y-4">
      <div className="flex items-center gap-3">
        <Clock className="h-5 w-5 text-[color:var(--sp-brand)]" />
        <h2 className="font-display text-xl font-black">Orari di apertura</h2>
      </div>
      <p className="text-sm text-[color:var(--sp-text-secondary)]">Puoi impostare due fasce per giorno (mattino e pomeriggio) per gestire la pausa pranzo. Gli slot fuori orario sono bloccati in agenda.</p>
      <div className="space-y-3">
        {DAY_KEYS.map((k, i) => {
          const wh = hours[k] || { closed: true };
          return (
            <div key={k} data-testid={`hours-row-${k}`} className="p-3 rounded-lg border border-[color:var(--sp-border)]">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-sm">{DAY_LABELS[i]}</span>
                <label className="text-xs flex items-center gap-2">
                  <input type="checkbox" checked={!wh.closed} onChange={(e) => setDay(k, { closed: !e.target.checked })} />
                  {wh.closed ? "Chiuso" : "Aperto"}
                </label>
              </div>
              {!wh.closed && (
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider mb-1">Mattino</div>
                    <div className="flex items-center gap-1">
                      <input type="time" className="sp-input !py-1 flex-1 text-xs" value={wh.open || ""} onChange={(e) => setDay(k, { open: e.target.value })} />
                      <span>→</span>
                      <input type="time" className="sp-input !py-1 flex-1 text-xs" value={wh.close || ""} onChange={(e) => setDay(k, { close: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider mb-1">Pomeriggio <span className="text-[color:var(--sp-text-muted)]">(opzionale)</span></div>
                    <div className="flex items-center gap-1">
                      <input type="time" className="sp-input !py-1 flex-1 text-xs" value={wh.open2 || ""} onChange={(e) => setDay(k, { open2: e.target.value })} />
                      <span>→</span>
                      <input type="time" className="sp-input !py-1 flex-1 text-xs" value={wh.close2 || ""} onChange={(e) => setDay(k, { close2: e.target.value })} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button data-testid="hours-save-btn" onClick={onSave} className="sp-btn-primary">Salva orari</button>
    </div>
  );
}

export function AccountCard({ user }) {
  const { t } = useTranslation();
  const [linking, setLinking] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { logout, updateUser } = useAuth();
  const nav = useNavigate();
  if (!user) return null;
  const providers = user.auth_providers || (user.auth_provider ? [user.auth_provider] : ["jwt"]);
  const hasGoogle = providers.includes("google");
  const hasEmail = providers.includes("jwt") || providers.includes("email") || !hasGoogle;

  const linkGoogle = () => {
    setLinking(true);
    const redirect = window.location.origin + "/app/settings#account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const doLogout = () => { logout(); nav("/login"); };

  const token = localStorage.getItem("sp_token");
  const displaySrc = user.avatar_url
    ? (user.avatar_url.startsWith("http") ? user.avatar_url : `${API}/files/${user.avatar_url}${token ? `?auth=${token}` : ""}`)
    : (user.picture || null);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/auth/me", { avatar_url: data.path });
      updateUser({ avatar_url: data.path });
      toast.success(t("settings.account.photoUpdated"));
    } catch (err) { toast.error(err?.response?.data?.detail || t("settings.account.uploadFailed")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAvatar = async () => {
    try {
      await api.patch("/auth/me", { avatar_url: null });
      updateUser({ avatar_url: null });
      toast.success(t("settings.account.photoRemoved"));
    } catch { toast.error(t("common.error")); }
  };

  return (
    <section data-testid="settings-account" className="sp-card p-8 space-y-4">
      <div className="flex items-center gap-3">
        <UserCircle2 className="h-5 w-5 text-[color:var(--sp-brand)]" />
        <h2 className="font-display text-xl font-black">{t("settings.sections.account")}</h2>
      </div>
      <div className="flex items-center gap-5">
        {displaySrc ? (
          <img src={displaySrc} alt="" className="h-16 w-16 rounded-full object-cover border border-[color:var(--sp-border)]" data-testid="account-avatar" />
        ) : (
          <div className="h-16 w-16 rounded-full bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] flex items-center justify-center font-black text-2xl" data-testid="account-avatar-fallback">
            {user.name?.[0]?.toUpperCase() || "?"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-lg">{user.name}</div>
          <div className="text-xs text-[color:var(--sp-text-muted)]">{user.email}</div>
          <div className="mt-2 flex gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={uploadAvatar} data-testid="account-avatar-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="account-avatar-upload-btn"
              className="sp-btn-ghost text-xs inline-flex items-center gap-1.5">
              <Upload className="h-3.5 w-3.5" />{uploading ? t("settings.account.uploading") : (user.avatar_url ? t("settings.account.changePhoto") : t("settings.account.uploadPhoto"))}
            </button>
            {user.avatar_url && (
              <button type="button" onClick={removeAvatar}
                data-testid="account-avatar-remove-btn"
                className="sp-btn-ghost text-xs inline-flex items-center gap-1.5 !text-[color:var(--sp-danger)]">
                <Trash2 className="h-3.5 w-3.5" /> {t("common.remove")}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="pt-3 border-t border-[color:var(--sp-border)]">
        <div className="label-eyebrow mb-3 flex items-center gap-1"><Link2 className="h-3 w-3" /> {t("settings.account.linkedLogins")}</div>
        <div className="space-y-2">
          <ProviderRow
            name="Email &amp; Password"
            desc={hasEmail ? t("settings.account.emailPasswordDesc") : t("settings.account.notLinked")}
            active={hasEmail}
            action={hasEmail && (
              <button
                data-testid="change-password-btn"
                onClick={() => setShowPwd(true)}
                className="sp-btn-ghost text-xs"
              >
                {t("settings.account.changePassword")}
              </button>
            )}
          />
          <ProviderRow
            name="Google"
            desc={hasGoogle ? t("settings.account.googleLinked") : t("settings.account.googleNotLinked")}
            active={hasGoogle}
            action={!hasGoogle && (
              <button
                data-testid="link-google-btn"
                onClick={linkGoogle}
                disabled={linking}
                className="sp-btn-ghost text-xs inline-flex items-center gap-1.5"
              >
                <GoogleIcon /> {t("settings.account.linkGoogle")}
              </button>
            )}
          />
        </div>
        <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-3">
          {t("settings.account.googleNote")}
        </div>
      </div>

      <LanguageCard />

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}

      <div className="pt-4 border-t border-[color:var(--sp-border)] flex justify-end">
        <button
          data-testid="logout-button"
          onClick={doLogout}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-200 bg-red-50 text-red-600 text-sm font-bold hover:bg-red-100 transition-colors"
        >
          <LogOut className="h-4 w-4" /> {t("common.logout")}
        </button>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Orari eccezionali (chiusure/aperture straordinarie + assenze/turni staff)
// ---------------------------------------------------------------------------
const emptyOverride = () => ({
  date: new Date().toISOString().slice(0, 10),
  kind: "closed",
  open: "10:00", close: "13:00", open2: "", close2: "",
  note: "",
  staff_id: "",  // "" = tutto il salone
});

function OverridesSection() {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyOverride());
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const [ov, st] = await Promise.all([
      api.get(`/hours-overrides?from=${today}`),
      api.get("/staff").catch(() => ({ data: [] })),
    ]);
    setItems(ov.data);
    setStaff(st.data);
  };
  useEffect(() => { load(); }, []);

  const open = (o = null) => {
    if (o) {
      setEditing(o.id);
      setForm({
        date: o.date, kind: o.kind,
        open: o.open || "", close: o.close || "", open2: o.open2 || "", close2: o.close2 || "",
        note: o.note || "",
        staff_id: o.staff_id || "",
      });
    } else {
      setEditing(null);
      setForm(emptyOverride());
    }
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, note: form.note || null, staff_id: form.staff_id || null };
      if (form.kind === "closed") { payload.open = null; payload.close = null; payload.open2 = null; payload.close2 = null; }
      else { payload.open2 = payload.open2 || null; payload.close2 = payload.close2 || null; }
      if (editing) await api.patch(`/hours-overrides/${editing}`, payload);
      else await api.post("/hours-overrides", payload);
      toast.success(editing ? "Aggiornato" : "Salvato");
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const del = async (o) => {
    try { await api.delete(`/hours-overrides/${o.id}`); toast.success("Rimosso"); load(); }
    catch { toast.error("Errore"); }
  };

  const fmt = (d) => {
    try { return new Date(d + "T00:00:00Z").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }); }
    catch { return d; }
  };

  const staffOf = (id) => staff.find((s) => s.id === id);

  const describeOverride = (o) => {
    const scope = o.staff_id ? (staffOf(o.staff_id)?.name || "collaboratore") : "Salone";
    if (o.kind === "closed") return `${scope} — chiuso${o.staff_id ? " / assente" : ""}`;
    return `${scope} — aperto ${o.open}–${o.close}${o.open2 ? ` · ${o.open2}–${o.close2}` : ""}`;
  };

  return (
    <div className="sp-card p-8 space-y-5" data-testid="overrides-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <CalendarPlus className="h-5 w-5 text-[color:var(--sp-brand)]" />
            <h2 className="font-display text-xl font-black">Orari eccezionali</h2>
          </div>
          <p className="text-sm text-[color:var(--sp-text-secondary)] mt-2 max-w-2xl">
            Aggiungi date fuori dal normale orario settimanale: chiusure straordinarie del salone, aperture extra, ferie o turni differenti per un singolo collaboratore. Sovrascrivono l&apos;orario abituale solo per la data indicata.
          </p>
        </div>
        <button data-testid="overrides-new-btn" onClick={() => open()} className="sp-btn-primary inline-flex items-center gap-2 text-sm">
          <Plus className="h-4 w-4" /> Nuovo
        </button>
      </div>

      {items.length === 0 ? (
        <div className="p-10 text-center text-sm text-[color:var(--sp-text-muted)] border border-dashed border-[color:var(--sp-border)] rounded-xl">
          Nessun orario eccezionale pianificato.
        </div>
      ) : (
        <div className="divide-y divide-[color:var(--sp-border)] rounded-xl border border-[color:var(--sp-border)] overflow-hidden">
          {items.map((o) => {
            const staffMember = o.staff_id ? staffOf(o.staff_id) : null;
            return (
              <div key={o.id} data-testid={`override-row-${o.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-[color:var(--sp-subtle)]">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${o.kind === "closed" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>
                  {o.kind === "closed" ? <X className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm capitalize">{fmt(o.date)}</span>
                    {staffMember ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ background: staffMember.color || "#3B82F6" }}>
                        {staffMember.name}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                        Tutto il salone
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">
                    {describeOverride(o)}
                    {o.note && <> · <span className="italic">{o.note}</span></>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`override-edit-${o.id}`} onClick={() => open(o)} className="p-1.5 rounded hover:bg-white text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`override-delete-${o.id}`} onClick={() => setConfirmDelete(o)} className="p-1.5 rounded hover:bg-white text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="sp-card w-full max-w-lg p-8 my-8 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-black">{editing ? "Modifica orario eccezionale" : "Nuovo orario eccezionale"}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <div>
              <label className="text-sm font-bold">Data</label>
              <input data-testid="override-date-input" required type="date" className="sp-input mt-1" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-bold block mb-2">Applica a</label>
              <select
                data-testid="override-scope-select"
                className="sp-input"
                value={form.staff_id}
                onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
              >
                <option value="">Tutto il salone</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.role_title ? ` — ${s.role_title}` : ""}</option>
                ))}
              </select>
              <p className="text-[11px] text-[color:var(--sp-text-muted)] mt-1">
                {form.staff_id
                  ? "Vale solo per il collaboratore selezionato (es. ferie, turno differente)."
                  : "Vale per l'intero salone (chiusura o apertura straordinaria)."}
              </p>
            </div>
            <div>
              <label className="text-sm font-bold block mb-2">Tipo</label>
              <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
                <button type="button" data-testid="override-kind-closed" onClick={() => setForm({ ...form, kind: "closed" })}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold ${form.kind === "closed" ? "bg-red-500 text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                  {form.staff_id ? "Ferie / Assenza" : "Chiusura straordinaria"}
                </button>
                <button type="button" data-testid="override-kind-open" onClick={() => setForm({ ...form, kind: "open" })}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold ${form.kind === "open" ? "bg-emerald-500 text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                  {form.staff_id ? "Turno straordinario" : "Apertura straordinaria"}
                </button>
              </div>
            </div>
            {form.kind === "open" && (
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider mb-1">Mattino</div>
                  <div className="flex items-center gap-1">
                    <input type="time" required className="sp-input !py-1 flex-1 text-xs" value={form.open} onChange={(e) => setForm({ ...form, open: e.target.value })} />
                    <span>→</span>
                    <input type="time" required className="sp-input !py-1 flex-1 text-xs" value={form.close} onChange={(e) => setForm({ ...form, close: e.target.value })} />
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider mb-1">Pomeriggio <span className="text-[color:var(--sp-text-muted)]">(opzionale)</span></div>
                  <div className="flex items-center gap-1">
                    <input type="time" className="sp-input !py-1 flex-1 text-xs" value={form.open2} onChange={(e) => setForm({ ...form, open2: e.target.value })} />
                    <span>→</span>
                    <input type="time" className="sp-input !py-1 flex-1 text-xs" value={form.close2} onChange={(e) => setForm({ ...form, close2: e.target.value })} />
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-bold">Nota (opzionale)</label>
              <input data-testid="override-note-input" placeholder="es. Ferie estive, festivo, evento…" className="sp-input mt-1" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <button data-testid="override-submit-btn" className="sp-btn-primary w-full">{editing ? "Salva modifiche" : "Aggiungi"}</button>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)} data-testid="override-confirm-dialog">
          <div className="sp-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-black mb-3">Rimuovere questo orario eccezionale?</h3>
            <div className="text-sm text-[color:var(--sp-text-secondary)] leading-relaxed">
              La data <b className="capitalize">{fmt(confirmDelete.date)}</b>{confirmDelete.staff_id ? " per il collaboratore" : ""} tornerà a seguire l&apos;orario abituale.
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmDelete(null)} className="sp-btn-ghost !py-1.5 !px-3 text-xs">Annulla</button>
              <button type="button"
                data-testid="override-confirm-delete-ok"
                onClick={async () => { await del(confirmDelete); setConfirmDelete(null); }}
                className="!py-1.5 !px-3 text-xs font-bold rounded-full bg-[color:var(--sp-danger)] text-white hover:opacity-90">
                Rimuovi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [conf, setConf] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast.error("Nuova password: minimo 8 caratteri"); return; }
    if (nw !== conf) { toast.error("Le due password non coincidono"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password aggiornata");
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="sp-card w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl font-black">Cambia password</h3>
        <div>
          <label className="text-sm font-bold">Password attuale</label>
          <input data-testid="pwd-current" type="password" autoComplete="current-password" className="sp-input mt-1" value={cur} onChange={(e) => setCur(e.target.value)} required autoFocus />
        </div>
        <div>
          <label className="text-sm font-bold">Nuova password</label>
          <input data-testid="pwd-new" type="password" autoComplete="new-password" minLength={8} className="sp-input mt-1" value={nw} onChange={(e) => setNw(e.target.value)} required />
          <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">Minimo 8 caratteri.</div>
        </div>
        <div>
          <label className="text-sm font-bold">Conferma nuova password</label>
          <input data-testid="pwd-confirm" type="password" autoComplete="new-password" className="sp-input mt-1" value={conf} onChange={(e) => setConf(e.target.value)} required />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="sp-btn-ghost flex-1">Annulla</button>
          <button data-testid="pwd-save" disabled={saving} className="sp-btn-primary flex-1">{saving ? "Salvo…" : "Salva password"}</button>
        </div>
      </form>
    </div>
  );
}

function ProviderRow({ name, desc, active, action }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-[color:var(--sp-border)] bg-white">
      <span className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
        <CheckCircle2 className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm">{name}</div>
        <div className="text-[11px] text-[color:var(--sp-text-muted)]">{desc}</div>
      </div>
      {action}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}
